/**
 * CredentialsStep.tsx
 * Step 1: Enter Mighty Networks API Key + Network ID.
 * Calls POST /api/validate-credentials on submit.
 */

import { useState, type FormEvent } from "react";
import { validateCredentials } from "../../lib/api";
import type { NetworkSummary } from "../../lib/types";
import Spinner from "../../components/Spinner";

interface Props {
  onValidated: (network: NetworkSummary) => void;
}

export default function CredentialsStep({ onValidated }: Props) {
  const [networkId, setNetworkId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await validateCredentials(apiKey, networkId);
      onValidated(result.network);
    } catch (err: any) {
      setError(err.message ?? "Something went wrong. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  }

  const canSubmit = networkId.trim().length > 0 && apiKey.trim().length > 0 && !loading;

  return (
    <div className="step-card">
      <div className="logo">
        <div className="logo-icon">⚡</div>
        <span className="logo-text">Hostology</span>
      </div>

      <h1 className="step-title">Connect your community</h1>
      <p className="step-sub">
        Enter your Mighty Networks credentials to mirror your community data and unlock portability.
      </p>

      {error && (
        <div className="error-banner" role="alert">
          ⚠️ {error}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="field">
          <label htmlFor="network-id">Network ID</label>
          <input
            id="network-id"
            type="text"
            inputMode="numeric"
            placeholder="e.g. 15852594"
            value={networkId}
            onChange={e => setNetworkId(e.target.value.trim())}
            disabled={loading}
            autoFocus
            autoComplete="off"
          />
          <p className="field-hint">
            Found in your Mighty Networks admin URL —{" "}
            <code>app.mn.co/networks/<strong>15852594</strong>/admin</code>
          </p>
        </div>

        <div className="field">
          <label htmlFor="api-key">API Key</label>
          <div className="input-with-action">
            <input
              id="api-key"
              type={showKey ? "text" : "password"}
              placeholder="Paste your Admin API key"
              value={apiKey}
              onChange={e => setApiKey(e.target.value.trim())}
              disabled={loading}
              autoComplete="off"
            />
            <button
              type="button"
              className="input-action-btn"
              onClick={() => setShowKey(v => !v)}
              aria-label={showKey ? "Hide API key" : "Show API key"}
            >
              {showKey ? "Hide" : "Show"}
            </button>
          </div>
          <p className="field-hint">
            Mighty Networks → Settings → Integrations → Admin API
          </p>
        </div>

        <button type="submit" className="btn-primary" disabled={!canSubmit}>
          {loading ? (
            <>
              <Spinner size={16} /> Verifying…
            </>
          ) : (
            "Verify & Continue →"
          )}
        </button>
      </form>

      <p className="security-note">🔒 Your API key is AES-256 encrypted at rest and never shared</p>
    </div>
  );
}
